import { useEffect, useId, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogInputStyle } from "@/components/ui/Dialog";
import { useDuplicateBoard } from "@/hooks/useDuplicateBoard";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: UUID;
  workspaceId: UUID;
  boardName: string;
}

export function DuplicateBoardDialog({
  open,
  onOpenChange,
  boardId,
  workspaceId,
  boardName,
}: Props) {
  const nameId = useId();
  const defaultName = `${boardName} Kopie`;
  const [name, setName] = useState(defaultName);
  const [error, setError] = useState<string | null>(null);
  const mutation = useDuplicateBoard();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !mutation.isPending;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      const board = await mutation.mutateAsync({ boardId, workspaceId, name: trimmed });
      onOpenChange(false);
      showToast(t.boardDuplicated, "success");
      void navigate({ to: "/b/$boardId", params: { boardId: board.id } });
    } catch (err) {
      setError(err instanceof Error ? err.message : t.unknownError);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.duplicateBoardTitle}
      description={t.duplicateBoardHint}
    >
      <form onSubmit={onSubmit}>
        <DialogField label={t.boardName} htmlFor={nameId} error={error ?? undefined}>
          <input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={255}
            style={dialogInputStyle}
            data-testid="duplicate-board-name"
          />
        </DialogField>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t.cancel}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!canSubmit}
            data-testid="duplicate-board-submit"
          >
            {mutation.isPending ? t.duplicating : t.duplicateBoardCta}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
